/**
 * Universal Size Selector Custom Element
 *
 * Provides functionality for switching between different size formats (US, UK, EU)
 * and updating the variant selector display accordingly.
 * Integrates with the variant selector to handle size selection and form updates.
 */

declare global {
  interface Window {
    $universalSizeMap: Record<string, {
      SIZE_UK: string;
      SIZE_US: string;
      SIZE_EU: string;
      SIZE_CM: string;
      AVAILABLE: boolean;
      AGE_GROUP: string | null;
      APPROX_AGE: string | null;
      WIDTH: string;
      CUSTOMER_FACING_NAME: string;
      COLOUR_NAME: string;
    }>;
    availableVariantWidths: Record<string, Set<string>>;
  }
}

export class UniversalSizeSelector extends HTMLElement {
  private selectedSizeType: string = 'US'
  private selectedAgeGroup: string = 'Younger'
  private sizeTypeButton: HTMLElement | null = null
  private sizeTypeLabel: HTMLElement | null = null
  private sizeTypeOptions: HTMLElement | null = null
  private sizeMetricInput: HTMLInputElement | null = null
  private widthInput: HTMLInputElement | null = null
  private utopiaColourInput: HTMLInputElement | null = null
  private ageGroupTabs: HTMLElement | null = null
  private sizeLabel: HTMLElement | null = null
  private productId: string = ''

  connectedCallback(): void {
    this.init()
    this.attachEventListeners()
    this.updateSizeDisplay()
    this.hideUnavailableAgeGroupTabs()
    this.filterVariantsByAgeGroup()
    this.initializeSelection()
  }

  private init(): void {
    this.sizeTypeButton = this.querySelector('[data-size-type-button]')
    this.sizeTypeLabel = this.querySelector('[data-size-type-label]')
    this.sizeTypeOptions = this.querySelector('[data-size-type-options]')
    this.sizeMetricInput = this.querySelector('[data-size-metric-input]')
    this.widthInput = this.querySelector('[data-width-input]')
    this.utopiaColourInput = this.querySelector('[data-utopia-colour-input]')
    this.ageGroupTabs = this.querySelector('[data-age-group-tabs]')
    this.sizeLabel = this.querySelector('[data-size-label]')

    this.selectedSizeType = this.dataset.selectedSize || 'US'
    this.productId = this.dataset.productId || ''

    const activeAgeTab = this.ageGroupTabs?.querySelector('[data-active]') as HTMLElement
    if (activeAgeTab) {
      this.selectedAgeGroup = activeAgeTab.dataset.ageGroup || 'Younger'
    }

    if (this.sizeTypeLabel) {
      this.sizeTypeLabel.textContent = this.selectedSizeType
    }
  }

  private attachEventListeners(): void {
    if (this.sizeTypeOptions) {
      this.sizeTypeOptions.addEventListener('click', (e) => {
        const button = (e.target as HTMLElement).closest('[data-size-type]') as HTMLElement
        if (button) {
          const newSizeType = button.dataset.sizeType
          if (newSizeType) {
            this.changeSizeType(newSizeType)
          }
        }
      })
    }

    if (this.ageGroupTabs) {
      this.ageGroupTabs.addEventListener('click', (e) => {
        const button = (e.target as HTMLElement).closest('[data-age-group]') as HTMLElement
        if (button) {
          const newAgeGroup = button.dataset.ageGroup
          if (newAgeGroup) {
            this.changeAgeGroup(newAgeGroup)
          }
        }
      })
    }

    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (variantGridContainer) {
      variantGridContainer.addEventListener('click', (e) => {
        const variantButton = (e.target as HTMLElement).closest('[data-variant-id]') as HTMLElement
        if (variantButton && variantButton.dataset.selectOption === 'true') {
          this.updateVariantActiveState(variantButton)
          this.updateSizeMetric()
          this.updateSizeLabelDisplay(variantButton)
          this.showWidthSelector(variantButton)
          this.autoSelectWidth()
        }
      })
    }

    // Add width button click handler
    const widthSelector = this.querySelector('[data-width-selector]')
    if (widthSelector) {
      widthSelector.addEventListener('click', (e) => {
        const widthButton = (e.target as HTMLElement).closest('[data-width-option]') as HTMLElement
        if (widthButton && !widthButton.hasAttribute('disabled')) {
          this.selectWidth(widthButton)
        }
      })
    }
  }

  private changeSizeType(newSizeType: string): void {
    this.selectedSizeType = newSizeType

    if (this.sizeTypeLabel) {
      this.sizeTypeLabel.textContent = newSizeType
    }

    this.updateSizeTypeActiveState()
    this.updateSizeDisplay()
    this.updateSizeMetric()

    // Update the size label if a variant is selected
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    const activeVariantButton = variantGridContainer?.querySelector('[data-variant-id].active') as HTMLElement
    if (activeVariantButton) {
      this.updateSizeLabelDisplay(activeVariantButton)
    }
  }

  private changeAgeGroup(newAgeGroup: string): void {
    this.selectedAgeGroup = newAgeGroup
    this.updateAgeGroupActiveState()
    this.filterVariantsByAgeGroup()
    this.clearSelection()
  }

  private clearSelection(): void {
    const widthButtons = this.querySelectorAll('[data-width-option]')
    widthButtons?.forEach((button) => {
      button.classList.remove('!bg-black', 'text-brand-white', '!font-medium')
    })

    const formModal = this.closest('x-modal')
    if (formModal) {
      formModal.removeAttribute('data-ui')
    }

    if (this.sizeLabel) {
      this.sizeLabel.innerHTML = '<span class="font-medium">Select Your Size</span>'
    }

    if (this.sizeMetricInput) {
      this.sizeMetricInput.value = ''
    }

    if (this.widthInput) {
      this.widthInput.value = ''
    }

    if (this.utopiaColourInput) {
      this.utopiaColourInput.value = ''
    }
  }

  private updateAgeGroupActiveState(): void {
    if (!this.ageGroupTabs) return

    const buttons = this.ageGroupTabs.querySelectorAll('[data-age-group]')
    buttons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const isActive = htmlButton.dataset.ageGroup === this.selectedAgeGroup

      if (isActive) {
        htmlButton.setAttribute('data-active', '')
      } else {
        htmlButton.removeAttribute('data-active')
      }
    })
  }

  private filterVariantsByAgeGroup(): void {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (!variantGridContainer || !window.$universalSizeMap) return

    const variantButtons = variantGridContainer.querySelectorAll('[data-variant-id]')

    variantButtons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const variantId = htmlButton.dataset.variantId

      if (variantId) {
        const variantData = window.$universalSizeMap[variantId]

        if (variantData) {
          const matchesAgeGroup = variantData.AGE_GROUP === this.selectedAgeGroup

          if (matchesAgeGroup) {
            htmlButton.style.display = ''
          } else {
            htmlButton.style.display = 'none'
          }
        }
      }
    })
  }

  private hideUnavailableAgeGroupTabs(): void {
    if (!this.ageGroupTabs || !window.$universalSizeMap) return

    const ageGroupButtons = this.ageGroupTabs.querySelectorAll('[data-age-group]')
    const ageGroupsWithSizes = new Set<string>()

    Object.values(window.$universalSizeMap).forEach((variantData) => {
      if (variantData.AGE_GROUP) {
        ageGroupsWithSizes.add(variantData.AGE_GROUP)
      }
    })

    let firstVisibleAgeGroup: string | null = null

    ageGroupButtons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const ageGroup = htmlButton.dataset.ageGroup

      if (ageGroup && !ageGroupsWithSizes.has(ageGroup)) {
        htmlButton.style.display = 'none'
      } else if (ageGroup && !firstVisibleAgeGroup) {
        firstVisibleAgeGroup = ageGroup
      }
    })

    if (!ageGroupsWithSizes.has(this.selectedAgeGroup) && firstVisibleAgeGroup) {
      this.selectedAgeGroup = firstVisibleAgeGroup
      this.updateAgeGroupActiveState()
    }
  }

  private isVariantInStock(size: string, width: string): boolean {
    const variantId = this.findVariantIdBySizeAndWidth(size, width)
    if (!variantId || !window.$universalSizeMap) return false

    const variantData = window.$universalSizeMap[variantId]
    return variantData?.AVAILABLE ?? false
  }

  private showWidthSelector(sizeButton: HTMLElement): void {
    const selectedSize = sizeButton.dataset.variantOption
    if (!selectedSize || !(window as any).availableVariantWidths) return

    const availableWidths = (window as any).availableVariantWidths[selectedSize]

    const widthSelector = this.querySelector('[data-width-selector]')
    widthSelector?.classList.remove('hidden')

    const widthButtons = this.querySelectorAll('[data-width-option]')
    widthButtons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const width = htmlButton.dataset.widthOption
      const widthExists = availableWidths?.has(width)

      if (!widthExists) {
        htmlButton.classList.add('bg-neutral-secondary', 'text-white')
        htmlButton.classList.remove('bg-neutral-extra-light', 'opacity-50')
        htmlButton.setAttribute('disabled', 'true')
        htmlButton.removeAttribute('data-out-of-stock')
      } else {
        const inStock = width && this.isVariantInStock(selectedSize, width)

        if (inStock) {
          htmlButton.classList.remove('bg-neutral-secondary', 'text-white', 'opacity-50')
          htmlButton.classList.add('bg-neutral-extra-light')
          htmlButton.removeAttribute('disabled')
          htmlButton.removeAttribute('data-out-of-stock')
        } else {
          htmlButton.classList.remove('bg-neutral-secondary', 'text-white')
          htmlButton.classList.add('bg-neutral-extra-light', 'opacity-50')
          htmlButton.setAttribute('disabled', 'true')
          htmlButton.setAttribute('data-out-of-stock', 'true')
        }
      }
    })
  }

  private initializeSelection(): void {
    const urlParams = new URLSearchParams(window.location.search)
    const variantIdFromUrl = urlParams.get('variant')

    if (variantIdFromUrl && window.$universalSizeMap) {
      const variantData = window.$universalSizeMap[variantIdFromUrl]
      if (variantData) {
        const size = `EURO ${variantData.SIZE_EU}/KIDS US ${variantData.SIZE_US}/KIDS UK ${variantData.SIZE_UK}`
        const width = variantData.WIDTH
        const ageGroup = variantData.AGE_GROUP

        if (ageGroup && ageGroup !== this.selectedAgeGroup) {
          this.selectedAgeGroup = ageGroup
          this.updateAgeGroupActiveState()
          this.filterVariantsByAgeGroup()
        }

        const variantGridContainer = this.querySelector('[data-variant-grid]')
        const sizeButton = variantGridContainer?.querySelector(`[data-variant-option="${size}"]`) as HTMLElement
        if (sizeButton) {
          this.updateVariantActiveState(sizeButton)
          this.updateSizeMetric()
          this.updateSizeLabelDisplay(sizeButton)
          this.showWidthSelector(sizeButton)

          const widthButton = this.querySelector(`[data-width-option="${width}"]`) as HTMLElement
          if (widthButton) {
            this.updateWidthButtonActiveState(widthButton)
          }

          this.updateProductProperties(variantIdFromUrl)
          this.updateFormState(variantIdFromUrl)
        }
      }
    }
  }

  private autoSelectWidth(): void {
    const widthButtons = this.querySelectorAll('[data-width-option]')

    let widthEButton: HTMLElement | null = null
    let smallestAvailableWidth: HTMLElement | null = null
    let smallestWidthValue: string | null = null

    widthButtons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const width = htmlButton.dataset.widthOption
      const isDisabled = htmlButton.hasAttribute('disabled')

      if (!isDisabled && width) {
        if (width === 'E') {
          widthEButton = htmlButton
        }

        if (!smallestAvailableWidth || (width < (smallestWidthValue || ''))) {
          smallestAvailableWidth = htmlButton
          smallestWidthValue = width
        }
      }
    })

    const targetWidth = widthEButton || smallestAvailableWidth
    if (targetWidth) {
      this.selectWidth(targetWidth)
    }
  }

  private selectWidth(widthButton: HTMLElement): void {
    const selectedWidth = widthButton.dataset.widthOption
    if (!selectedWidth) return

    const variantGridContainer = this.querySelector('[data-variant-grid]')
    const activeSizeButton = variantGridContainer?.querySelector('[data-variant-id].active') as HTMLElement
    if (!activeSizeButton) return

    const selectedSize = activeSizeButton.dataset.variantOption

    const variantId = this.findVariantIdBySizeAndWidth(selectedSize, selectedWidth)
    if (!variantId) return

    const variantSelector = this.querySelector('variant-selector')
    const hiddenSelect = variantSelector?.querySelector('select[name="id"]') as HTMLSelectElement
    if (hiddenSelect) {
      hiddenSelect.value = variantId
      hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }))
    }

    const pairFormId = this.dataset.pairFormId
    if (pairFormId) {
      const pairedElement = document.getElementById(pairFormId)
      if (pairedElement) {
        const pairedSelect = pairedElement.querySelector('select[name="id"]') as HTMLSelectElement
        if (pairedSelect) {
          pairedSelect.value = variantId
          pairedSelect.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }
    }

    if (this.hasAttribute('data-url-sync')) {
      window.history.replaceState({}, '', '?variant=' + variantId)
    }

    this.updateWidthButtonActiveState(widthButton)
    this.updateProductProperties(variantId)
    this.updateFormState(variantId)
  }

  private updateProductProperties(variantId: string): void {
    if (!window.$universalSizeMap || !variantId) return

    const variantData = window.$universalSizeMap[variantId]
    if (!variantData) return

    if (this.widthInput) {
      this.widthInput.value = variantData.WIDTH || ''
    }

    if (this.utopiaColourInput) {
      this.utopiaColourInput.value = variantData.COLOUR_NAME || ''
    }
  }

  private findVariantIdBySizeAndWidth(size: string | undefined, width: string): string | null {
    if (!size || !window.$universalSizeMap) return null

    // Loop through all variants to find one matching both size and width
    for (const [variantId, variantData] of Object.entries(window.$universalSizeMap)) {
      // Check if this variant matches the selected size
      const variantSize = `EURO ${variantData.SIZE_EU}/KIDS US ${variantData.SIZE_US}/KIDS UK ${variantData.SIZE_UK}`

      if (variantSize === size && variantData.WIDTH === width) {
        return variantId
      }
    }

    return null
  }

  private updateWidthButtonActiveState(selectedButton: HTMLElement): void {
    const widthButtons = this.querySelectorAll('[data-width-option]')

    widthButtons.forEach((button) => {
      button.classList.remove('!bg-black', 'text-brand-white', '!font-medium')
    })

    selectedButton.classList.add('!bg-black', 'text-brand-white', '!font-medium')

    const selectWidthLabel = document.querySelector('.select-width-label')
    if (selectWidthLabel) {
      selectWidthLabel.innerHTML = `<span class="select-width-label paragraph-m"><span class="font-medium">Width </span> ${selectedButton.dataset.widthOption || 'Width'}</span>`
    }
  }

  private updateSizeTypeActiveState(): void {
    if (!this.sizeTypeOptions) return

    const buttons = this.sizeTypeOptions.querySelectorAll('[data-size-type]')
    buttons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const isActive = htmlButton.dataset.sizeType === this.selectedSizeType

      htmlButton.classList.toggle('font-bold', isActive)
      htmlButton.classList.toggle('bg-primary', isActive)
      htmlButton.classList.toggle('text-secondary', isActive)
    })
  }

  private updateSizeDisplay(): void {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (!variantGridContainer || !window.$universalSizeMap) return

    const variantButtons = variantGridContainer.querySelectorAll('[data-variant-id]')

    variantButtons.forEach((button) => {
      const htmlButton = button as HTMLElement
      const variantId = htmlButton.dataset.variantId

      if (variantId) {
        const variantData = window.$universalSizeMap[variantId]

        if (variantData) {
          let displaySize = ''

          switch (this.selectedSizeType) {
            case 'UK':
              displaySize = variantData.SIZE_UK
              break
            case 'EU':
              displaySize = variantData.SIZE_EU
              break
            case 'US':
            default:
              displaySize = variantData.SIZE_US
              break
          }

          htmlButton.style.setProperty('--display-size', `"${displaySize}"`)
        }
      }
    })
  }

  private updateVariantActiveState(selectedButton: HTMLElement): void {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    if (!variantGridContainer) return

    const allButtons = variantGridContainer.querySelectorAll('[data-variant-id]')

    allButtons.forEach((button) => {
      button.classList.remove('active')
    })

    selectedButton.classList.add('active')
  }

  private updateSizeMetric(): void {
    if (!this.sizeMetricInput) return

    const variantGridContainer = this.querySelector('[data-variant-grid]')
    const activeVariantButton = variantGridContainer?.querySelector('[data-variant-id].active') as HTMLElement
    if (!activeVariantButton) return

    const variantId = activeVariantButton.dataset.variantId
    if (!variantId) return

    const variantData = window.$universalSizeMap?.[variantId]
    if (!variantData) return

    let sizeValue = ''

    switch (this.selectedSizeType) {
      case 'UK':
        sizeValue = `UK ${variantData.SIZE_UK}`
        break
      case 'EU':
        sizeValue = `EURO ${variantData.SIZE_EU}`
        break
      case 'US':
      default:
        sizeValue = `US ${variantData.SIZE_US}`
        break
    }

    this.sizeMetricInput.value = sizeValue
  }

  private updateSizeLabelDisplay(selectedButton: HTMLElement): void {
    if (!this.sizeLabel) return

    const variantId = selectedButton.dataset.variantId
    if (!variantId) return

    const variantData = window.$universalSizeMap?.[variantId]
    if (!variantData) return

    let sizeValue = ''

    switch (this.selectedSizeType) {
      case 'UK':
        sizeValue = variantData.SIZE_UK
        break
      case 'EU':
        sizeValue = variantData.SIZE_EU
        break
      case 'US':
      default:
        sizeValue = variantData.SIZE_US
        break
    }

    // Update label to show "Size {value}" with Size in bold
    // Removed the out of stock label - validation will happen on form submit instead
    this.sizeLabel.innerHTML = `<span class="font-medium">Size</span> ${sizeValue}`
  }

  /**
   * Public method to get current size type
   */
  public getSizeType(): string {
    return this.selectedSizeType
  }

  /**
   * Public method to set size type programmatically
   */
  public setSizeType(sizeType: string): void {
    if (['US', 'UK', 'EU'].includes(sizeType)) {
      this.changeSizeType(sizeType)
    }
  }

  /**
   * Update the form modal's data-ui state based on variant availability
   */
  private updateFormState(variantId: string): void {
    if (!window.$universalSizeMap || !variantId) return

    const variantData = window.$universalSizeMap[variantId]
    if (!variantData) return

    const formModal = this.closest('x-modal')
    if (!formModal) return

    if (variantData.AVAILABLE) {
      formModal.setAttribute('data-ui', 'visible')
    } else {
      formModal.setAttribute('data-ui', 'disabled')
    }
  }

  /**
   * Public method to get selected variant ID
   */
  public getSelectedVariantId(): string | null {
    const variantGridContainer = this.querySelector('[data-variant-grid]')
    const activeVariantButton = variantGridContainer?.querySelector('[data-variant-id].active') as HTMLElement
    return activeVariantButton?.dataset.variantId || null
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const globalDefaultSize = (window as any).product_default_size
  if (globalDefaultSize) {
    const selectors = document.querySelectorAll('universal-size-selector')
    selectors.forEach((selector) => {
      const element = selector as UniversalSizeSelector
      element.setSizeType(globalDefaultSize)
    })
  }
})

export default UniversalSizeSelector
